import { useState, useCallback } from 'react';
import { ICommandResponse } from '../api/commands';
import {
  IEmailCommand,
  IEmailCommandPayload
} from '@publishing-engine/domain/types/email-template';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';

interface UseSendReviewerInvitationsParams {
  ensureUser: (email: string) => Promise<{
    message: string;
    email: string;
    userId: string;
  }>;
  availableActions: IAvailableWorkflowActions;
  inviteReviewerMutation: {
    mutateAsync: (payload: any) => Promise<ICommandResponse>;
  };
  requestReviewInRevisionMutation: {
    mutateAsync: (payload: any) => Promise<ICommandResponse>;
  };
  toast: (options: {
    variant: 'success' | 'error';
    title: string;
    description: string;
    isClosable: boolean;
  }) => void;
}

export const useSendReviewerInvitations = ({
  ensureUser,
  availableActions,
  inviteReviewerMutation,
  requestReviewInRevisionMutation,
  toast
}: UseSendReviewerInvitationsParams) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const sendReviewerInvitations = useCallback(
    async (
      reviewerEmails: string[],
      emailContext: IEmailCommand,
      closeOnSuccess: () => void
    ): Promise<number> => {
      if (isProcessing) return 0;
      setIsProcessing(true);

      let usersResponse: {
        message: string;
        email: string;
        userId: string;
      }[];

      try {
        usersResponse = await Promise.all(
          reviewerEmails.map(email => ensureUser(email))
        );
      } catch (e: any) {
        toast({
          variant: 'error',
          title: 'Invites Failed',
          description:
            e?.response?.data?.message ||
            'We are unable to invite these reviewers at this time.',
          isClosable: true
        });
        setIsProcessing(false);
        return 0;
      }

      const results: ICommandResponse[] = [];

      const mutation = availableActions.contains('RequestReviewInRevision')
        ? requestReviewInRevisionMutation
        : inviteReviewerMutation;

      for (const { userId, email } of usersResponse) {
        const emails: IEmailCommandPayload = [
          {
            ...emailContext,
            recipients: {
              ...emailContext.recipients,
              to: [email]
            }
          }
        ];

        results.push(
          await mutation.mutateAsync({
            reviewer: { id: userId },
            emails
          })
        );
      }

      const successCount = results.filter(r => r.isSuccess).length;
      const failedCount = results.length - successCount;

      if (failedCount > 0) {
        toast({
          variant: 'error',
          title: 'Invites Failed',
          description: `${failedCount} Reviewers Failed to Invite, Please try again.`,
          isClosable: true
        });
      }

      if (successCount > 0) {
        toast({
          variant: 'success',
          title: 'Invitation Sent',
          description: `Invitation email sent to (${successCount}) reviewers.`,
          isClosable: true
        });
        closeOnSuccess();
      }

      setIsProcessing(false);
      return successCount;
    },
    [
      isProcessing,
      ensureUser,
      availableActions,
      inviteReviewerMutation,
      requestReviewInRevisionMutation,
      toast
    ]
  );

  return {
    sendReviewerInvitations,
    isProcessing
  };
};
